/* eslint-disable react-hooks/exhaustive-deps */
import Loader from 'components/atoms/loader';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  getNotificationProgress,
  updateNotificationProgress,
  deleteProgressBatch,
  markReadProgressBatch,
  markUnreadProgressBatch,
} from 'store/modules/management_store/actions';
import { showUndoToast } from 'store/modules/undo_toast_store/actions';
import { UndoToastState } from 'store/modules/undo_toast_store/types';
import BatchConfirmModal from 'components/atoms/batch_confirm_modal';
import BatchMarkReadModal from 'components/atoms/batch_mark_read_modal';
import createTrack from 'services/actions/tracker_actions';
import { ManagementState } from 'store/modules/management_store/types';
import companyId from 'services/company_id';
import { ProfileState } from 'store/modules/profile_store/types';
import tabId from 'services/tabId';
import ItemShare from 'components/atoms/item_share';
import SelectButton from 'components/atoms/select_button';
import Pagination from 'components/atoms/pagination';
import PerPageOptions from 'components/atoms/per_page_options';
import { useReactToPrint } from 'react-to-print';
import { toggleSelected } from 'services/toggleSelected';
import { getDownloadBiddings } from 'services/actions/bidding_actions';
import useRefetchOnModalMutation from 'hooks/useRefetchOnModalMutation';
import ManagementTask from '../management_task';
import ProgressFilter from '../progress_filter';
import { datatableColumns, datatableData } from './datatable_list';
import { ContainerProgressPage } from './styles';
import ItemTools from '../item_tools';

const DEFAULT_PER_PAGE = 20;

const ProgressPage = () => {
  const dispatch = useDispatch();

  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const canShowDeleteButton = !!profile.consultant_login;

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const perPageOptions = [
    { value: 20, label: 20 },
    { value: 40, label: 40 },
    { value: 50, label: 50 },
    { value: 60, label: 60 },
    { value: 80, label: 80 },
    { value: 100, label: 100 },
  ];

  const handlePerPageChange = (value?: number) => {
    setPerPage(value || DEFAULT_PER_PAGE);
    setPage(1);
  };

  const [selectedNotificationProgressId, setSelectedNotificationProgressId] = useState<number>(-1);
  const [modalManagementTaskOpened, setModalManagementTaskOpened] = useState<boolean>(false);
  const [filterRead, setFilterRead] = useState<boolean>(true);
  const [filterUnread, setFilterUnread] = useState<boolean>(true);
  const [wholePageSelected, setWholePageSelected] = useState<boolean>(false);
  const [selectedProgress, setSelectedProgress] = useState<Array<number>>([]);
  const [scrollToolsClass, setScrollToolsClass] = useState<string>('');
  const [showShareWindow, setShowShareWindow] = useState<boolean>(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState<boolean>(false);
  const [showBatchMarkAsModal, setShowBatchMarkAsModal] = useState<boolean>(false);
  const [batchRequestedCount, setBatchRequestedCount] = useState<number>(0);
  const [batchRemovedCount, setBatchRemovedCount] = useState<number | null>(null);

  const { lastUndo } = useSelector<ApplicationState, UndoToastState>(
    (state) => state.session.undoToast,
  );
  const initialUndoAtRef = useRef<number | undefined>(lastUndo?.at);

  const canShowMarkAsButton = !!profile.consultant_login;

  const readFilter = useMemo<boolean | null | '__none__'>(() => {
    if (!filterRead && !filterUnread) return '__none__';
    if (filterRead && filterUnread) return null;
    if (filterRead) return true;
    return false;
  }, [filterRead, filterUnread]);

  const list = management?.notification_progress || [];

  const totalEntries =
    management.notification_progress_total_entries || management.notification_progress?.length || 0;

  const selectedUnreadIds = list
    .filter((notif) => selectedProgress.includes(notif.id) && !notif.read)
    .map((notif) => notif.id);
  const selectedReadIds = list
    .filter((notif) => selectedProgress.includes(notif.id) && notif.read)
    .map((notif) => notif.id);

  const buildBatchSuccessMessage = (requested: number, removed: number | null) => {
    const effectiveRemoved = removed ?? requested;

    if (effectiveRemoved <= 0) {
      return 'Nenhum andamento seu foi removido.';
    }

    const ownedLabel =
      effectiveRemoved === 1 ? 'Andamento removido!' : `${effectiveRemoved} andamentos removidos!`;

    if (removed !== null && removed < requested) {
      const skipped = requested - removed;
      const skippedLabel =
        skipped === 1
          ? 'pertencia a outro membro e foi mantido'
          : 'pertenciam a outros membros e foram mantidos';
      return `${ownedLabel} ${skipped} ${skippedLabel}.`;
    }

    return ownedLabel;
  };
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const downloadFile = (
    fileExtension: string,
    biddings_managed?: boolean,
    biddings_finished?: boolean,
    biddings_progress?: boolean,
  ) => {
    const biddingIds = list
      .filter((item) => selectedProgress.includes(item.id))
      .map((item) => item.bidding_id);

    getDownloadBiddings(
      fileExtension,
      'licitacoes',
      biddingIds,
      selectedProgress?.length,
      companyId(profile),
      biddings_managed,
      biddings_finished,
      biddings_progress,
      selectedProgress,
      false,
    );
  };

  const toggleSelectedProgress = (id: number, updateAll = false) => {
    toggleSelected({
      id,
      selectedList: selectedProgress,
      setSelectedList: setSelectedProgress,
      sourceList: list,
      wholePageSelected,
      updateAll,
    });
  };
  const handleShare = () => {
    setShowShareWindow(!showShareWindow);
  };
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'conlicitacao',
  });

  const wrapStatusSetter =
    (setter: Dispatch<SetStateAction<boolean>>): Dispatch<SetStateAction<boolean>> =>
    (next) => {
      setter(next);
      setPage(1);
    };

  const fetchProgress = () => {
    dispatch(
      getNotificationProgress(companyId(profile), null, {
        page,
        per_page: perPage,
        read: readFilter,
      }),
    );
  };

  useEffect(() => {
    const windowDiv = document.getElementById('windowDiv');
    if (!windowDiv) return undefined;
    const handler = () => {
      setScrollToolsClass(windowDiv.scrollTop > 10 ? 'border-bottom' : '');
    };
    windowDiv.addEventListener('scroll', handler);
    return () => windowDiv.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    if (!list) return;
    setWholePageSelected(
      list.length > 0 && list.every((item) => selectedProgress.includes(item.id)),
    );
  }, [selectedProgress, list]);

  useEffect(() => {
    fetchProgress();
    setSelectedProgress([]);
    setWholePageSelected(false);
  }, [filterRead, filterUnread, page, perPage, profile.currentCompany[tabId()]]);

  useEffect(() => {
    if (!lastUndo || lastUndo.at === initialUndoAtRef.current) return;
    if (lastUndo.type === 'favorites') return;
    fetchProgress();
  }, [lastUndo?.at]);

  useEffect(() => {
    if (modalManagementTaskOpened) return;
    setSelectedNotificationProgressId(-1);
  }, [modalManagementTaskOpened]);

  useRefetchOnModalMutation(
    modalManagementTaskOpened,
    [
      request.update_progress?.loaded,
      request.delete_progress?.loaded,
      request.create_progress_comment?.loaded,
      request.create_progress_attachment?.loaded,
    ],
    () => {
      setSelectedProgress([]);
      setWholePageSelected(false);
      fetchProgress();
    },
  );

  useEffect(() => {
    if (selectedNotificationProgressId < 0) return;
    const selected = list.find((notif) => notif.id === selectedNotificationProgressId);
    if (!selected) return;
    setModalManagementTaskOpened(true);
    if (!selected.read) dispatch(updateNotificationProgress(selected.id));
  }, [selectedNotificationProgressId]);

  const selectedBiddings = list.filter((b) => selectedProgress.includes(b.id));
  const selectedProgressBiddingIds = Array.from(
    new Set(selectedBiddings.map((notif) => notif.bidding_id)),
  );
  const printColumns = [
    ...datatableColumns(
      () => {},
      () => {},
    ).filter((col) => col.name !== ''),
    {
      name: 'Status',
      selector: (row: any) => (row.read ? 'Lido' : 'Não lido'),
      cell: (row: any) => (
        <div style={{ fontWeight: row.read ? 'normal' : 'bold' }}>
          {row.read ? 'Lido' : 'Não lido'}
        </div>
      ),
      sortable: true,
      width: '100px',
    },
  ];

  const customStyles = {
    headCells: { style: { paddingLeft: '10px', paddingRight: '10px' } },
    cells: { style: { paddingLeft: '10px', paddingRight: '10px' } },
    rows: { style: { fontSize: '14px' } },
  };

  const conditionalRowStyles = [
    { when: (row: any) => row.read === true, style: { backgroundColor: '#E0E3E5' } },
  ];

  const selectedNotif = list.find((notif) => notif.id === selectedNotificationProgressId);

  return (
    <div className="col-md-12">
      <div className="py-3 d-md-flex">
        <ProgressFilter
          filterRead={filterRead}
          setFilterRead={wrapStatusSetter(setFilterRead)}
          filterUnread={filterUnread}
          setFilterUnread={wrapStatusSetter(setFilterUnread)}
        />
      </div>
      <div>
        <ContainerProgressPage>
          {request.get_notification_progress.loading && <Loader />}
          <div className={`row list-tools py-1 ${scrollToolsClass}`}>
            <div className="position-relative col-md-6 d-block py-3 px-2 tour-remove-favorites-select-begin">
              <SelectButton
                isChecked={wholePageSelected}
                id={0}
                toggleSelected={toggleSelectedProgress}
                text="Selecionar página"
              />
              {selectedProgress?.length > 0 && (
                <div className="itemToolsSelected ml-3">
                  {selectedProgress?.length}{' '}
                  {selectedProgress?.length === 1
                    ? 'registro selecionado.'
                    : 'registros selecionados.'}
                  <button
                    type="button"
                    className="btn btn-link m-0 py-0 px-1 border-0"
                    onClick={() => setSelectedProgress([])}
                  >
                    Limpar seleção.
                  </button>
                </div>
              )}
            </div>
            <ItemTools
              enabledButtons={selectedProgress?.length > 0}
              biddings_progress
              disabledPopoverLabel="andamento"
              downloadFile={downloadFile}
              handleShare={handleShare}
              handlePrint={handlePrint}
              showDeleteButton={canShowDeleteButton}
              handleDelete={canShowDeleteButton ? () => setShowBatchDeleteModal(true) : undefined}
              showMarkAsReadButton={canShowMarkAsButton}
              handleMarkAsRead={
                canShowMarkAsButton ? () => setShowBatchMarkAsModal(true) : undefined
              }
            />
            {showShareWindow && (
              <ItemShare
                setShowShareWindow={setShowShareWindow}
                selectedItems={selectedProgressBiddingIds}
              />
            )}
            {canShowDeleteButton && showBatchDeleteModal && (
              <BatchConfirmModal
                modalOpened={showBatchDeleteModal}
                setModalOpened={setShowBatchDeleteModal}
                title="Remover andamentos"
                message={
                  selectedProgress.length === 1
                    ? 'Tem certeza que deseja remover este andamento? O registro de andamento da licitação também será removido.'
                    : `Tem certeza que deseja remover ${selectedProgress.length} andamentos? Os registros de andamento das licitações também serão removidos.`
                }
                confirmLabel={
                  selectedProgress.length === 1
                    ? 'Remover Andamento'
                    : `Remover ${selectedProgress.length} Andamentos`
                }
                successMessage={buildBatchSuccessMessage(batchRequestedCount, batchRemovedCount)}
                errorMessage="Erro ao remover os andamentos."
                variant="danger"
                isLoading={request.delete_progress.loading}
                isLoaded={request.delete_progress.loaded}
                isError={request.delete_progress.error}
                onConfirm={() => {
                  createTrack('Gestão de Licitações - Removeu andamentos em lote');
                  setBatchRequestedCount(selectedProgress.length);
                  setBatchRemovedCount(null);
                  const result: any = dispatch(deleteProgressBatch(selectedProgress));
                  if (result && typeof result.then === 'function') {
                    result.then((response: any) => {
                      if (!response || response.status >= 400) return;
                      const ids = response?.data?.removed_notification_ids;
                      const deletedAt = response?.data?.deleted_at;
                      if (Array.isArray(ids)) setBatchRemovedCount(ids.length);
                      if (Array.isArray(ids) && ids.length > 0 && deletedAt) {
                        const message =
                          ids.length === 1
                            ? '1 andamento removido.'
                            : `${ids.length} andamentos removidos.`;
                        dispatch(
                          showUndoToast({
                            type: 'progress',
                            ids,
                            deletedAt,
                            message,
                            companyId: companyId(profile),
                          }),
                        );
                      }
                      fetchProgress();
                    });
                  }
                }}
                onSuccess={() => setSelectedProgress([])}
              />
            )}
            {canShowMarkAsButton && showBatchMarkAsModal && (
              <BatchMarkReadModal
                modalOpened={showBatchMarkAsModal}
                setModalOpened={setShowBatchMarkAsModal}
                unreadCount={selectedUnreadIds.length}
                readCount={selectedReadIds.length}
                isLoading={
                  !!request.mark_read_progress_batch?.loading ||
                  !!request.mark_unread_progress_batch?.loading
                }
                isLoaded={
                  !!request.mark_read_progress_batch?.loaded ||
                  !!request.mark_unread_progress_batch?.loaded
                }
                isError={
                  !!request.mark_read_progress_batch?.error ||
                  !!request.mark_unread_progress_batch?.error
                }
                onConfirm={(mode) => {
                  let p: any;
                  if (mode === 'read') {
                    createTrack('Gestão de Licitações - Marcou andamentos como lido em lote');
                    p = dispatch(markReadProgressBatch(selectedUnreadIds));
                  } else {
                    createTrack('Gestão de Licitações - Marcou andamentos como não lido em lote');
                    p = dispatch(markUnreadProgressBatch(selectedReadIds));
                  }
                  if (p && typeof p.then === 'function') {
                    p.then((response: any) => {
                      if (!response || response.status >= 400) return;
                      const ids =
                        mode === 'read'
                          ? response?.data?.read_notification_progress_ids
                          : response?.data?.unread_notification_progress_ids;
                      if (Array.isArray(ids) && ids.length > 0) {
                        const statusLabel = mode === 'read' ? 'lido' : 'não lido';
                        const message =
                          ids.length === 1
                            ? `1 andamento marcado como ${statusLabel}.`
                            : `${ids.length} andamentos marcados como ${statusLabel}s.`;
                        dispatch(
                          showUndoToast({
                            type: mode === 'read' ? 'progress_read' : 'progress_unread',
                            ids,
                            message,
                            companyId: companyId(profile),
                          }),
                        );
                      }
                      fetchProgress();
                    });
                  }
                }}
                onSuccess={() => setSelectedProgress([])}
              />
            )}
            {request.get_notification_progress.loaded && (
              <div className="row mb-3 px-4">
                <PerPageOptions
                  options={perPageOptions}
                  postsPerPage={perPage}
                  handleSelect={handlePerPageChange}
                />
              </div>
            )}
            {request.get_notification_progress.loaded && (
              <DataTable
                columns={datatableColumns(
                  setSelectedNotificationProgressId,
                  toggleSelectedProgress,
                )}
                data={datatableData(list, companyId(profile), selectedProgress)}
                customStyles={customStyles}
                noDataComponent={<div className="p-5">Você não possui andamentos.</div>}
                conditionalRowStyles={conditionalRowStyles}
              />
            )}
            {request.get_notification_progress.loaded && totalEntries > perPage && (
              <div className="col-12 d-flex justify-content-end mt-4">
                <Pagination
                  postsPerPage={perPage}
                  totalPosts={totalEntries}
                  paginate={(p) => setPage(p)}
                  currentPage={page}
                  firstAndLastPage
                />
              </div>
            )}
          </div>
          {modalManagementTaskOpened && selectedNotif && (
            <ManagementTask
              modalManagementTaskOpened={modalManagementTaskOpened}
              setModalManagementTaskOpened={setModalManagementTaskOpened}
              bidding={selectedNotif.bidding}
              taskType="progress"
              task={selectedNotif.progress}
            />
          )}
        </ContainerProgressPage>
        <div style={{ display: 'none' }}>
          <div ref={componentRef}>
            <DataTable
              columns={printColumns}
              data={datatableData(selectedBiddings, companyId(profile), selectedProgress)}
              customStyles={{
                rows: { style: { fontSize: '12px' } },
                headCells: { style: { fontWeight: 'bold' } },
              }}
              pagination={false}
              selectableRows={false}
              dense
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressPage;
